import type { CalendarDay, CalendarEntry } from '@klopotun/contracts';
import { type CivilDate, formatCivilDate, formatMoney, toCivilDate } from '@klopotun/domain';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMeQuery } from '../../entities/me/index.js';
import { useCalendarQuery } from '../../entities/summary/index.js';
import { MonthGrid } from '../../features/calendar/index.js';
import { paymentDetailPath } from '../../features/navigation/index.js';
import {
  formatMonthName,
  formatStoredDateForPeople,
  VARIABLE_AMOUNT_TEXT,
} from '../../features/payment-view/index.js';
import {
  formatApproximate,
  formatBaseMoney,
  formatCalendarState,
  formatMoneyMaybeApproximate,
  formatMonthTitle,
  shiftMonth,
} from '../../features/summary-view/index.js';
import {
  EmptyState,
  ErrorState,
  List,
  ListItem,
  PageHeader,
  PeriodSwitcher,
  Screen,
  Spinner,
} from '../../shared/ui/index.js';
import styles from './calendar-page.module.css';

type YearMonth = { year: number; month: number };

const LoadingScreen = () => (
  <Screen>
    <div className={styles.centered}>
      <Spinner label="Завантаження" />
    </div>
  </Screen>
);

type FailureScreenProps = {
  message: string;
  onRetry: () => void;
};

const FailureScreen = ({ message, onRetry }: FailureScreenProps) => (
  <Screen>
    <ErrorState title="Не вдалося завантажити календар" message={message} onRetry={onRetry} />
  </Screen>
);

const monthPrefixOf = (shown: YearMonth): string =>
  `${String(shown.year).padStart(4, '0')}-${String(shown.month).padStart(2, '0')}-`;

const isSameMonth = (shown: YearMonth, date: CivilDate): boolean =>
  shown.year === date.year && shown.month === date.month;

const amountOf = (entry: CalendarEntry): string =>
  entry.amountMinor === null
    ? VARIABLE_AMOUNT_TEXT
    : formatMoney({ amountMinor: entry.amountMinor, currency: entry.currency });

const equivalentOf = (entry: CalendarEntry, baseCurrency: string): string | undefined => {
  const isEstimate = entry.amountMinor === null;
  const isConverted = entry.currency !== baseCurrency;
  const addsToAmount = isEstimate || isConverted;

  if (!addsToAmount || entry.baseAmountMinor === null) {
    return undefined;
  }

  const equivalent = formatBaseMoney(entry.baseAmountMinor, baseCurrency);

  return entry.isBaseAmountExact ? equivalent : formatApproximate(equivalent);
};

type DayEntriesProps = {
  day: CalendarDay | undefined;
  baseCurrency: string;
  onOpen: (paymentId: string) => void;
};

const DayEntries = ({ day, baseCurrency, onOpen }: DayEntriesProps) => {
  if (day === undefined) {
    return <EmptyState title="У цей день нічого не списується" description="Оберіть інший день." />;
  }

  return (
    <List
      header={formatStoredDateForPeople(day.date)}
      footer={`Разом ${formatMoneyMaybeApproximate(day.totalMinor, baseCurrency, !day.isComplete)}`}
    >
      {day.entries.map((entry) => (
        <ListItem
          key={entry.occurrenceId ?? `${entry.paymentId}-forecast`}
          title={entry.name}
          subtitle={formatCalendarState(entry.state)}
          value={amountOf(entry)}
          valueNote={equivalentOf(entry, baseCurrency)}
          chevron
          onClick={() => {
            onOpen(entry.paymentId);
          }}
        />
      ))}
    </List>
  );
};

export const CalendarPage = () => {
  const [shownMonth, setShownMonth] = useState<YearMonth | null>(null);
  const [chosenDate, setChosenDate] = useState<string | null>(null);
  const navigate = useNavigate();
  const me = useMeQuery();

  const timeZone = me.data?.settings.timezone;
  const today = timeZone === undefined ? null : toCivilDate(new Date(), timeZone);
  const shown: YearMonth | null =
    shownMonth ?? (today === null ? null : { year: today.year, month: today.month });
  const calendar = useCalendarQuery(shown?.year ?? null);

  if (me.isError) {
    return (
      <FailureScreen
        message={me.error.message}
        onRetry={() => {
          void me.refetch();
        }}
      />
    );
  }

  if (calendar.isError) {
    return (
      <FailureScreen
        message={calendar.error.message}
        onRetry={() => {
          void calendar.refetch();
        }}
      />
    );
  }

  if (shown === null || today === null || calendar.isPending) {
    return <LoadingScreen />;
  }

  const loaded = calendar.data;
  const prefix = monthPrefixOf(shown);
  const daysOfMonth = loaded.days.filter((day) => day.date.startsWith(prefix));
  const daysByDate = new Map(daysOfMonth.map((day) => [day.date, day] as const));
  const todayIso = formatCivilDate(today);
  // The chosen day is kept only while it belongs to the month on screen; otherwise today, when
  // the month is this one, and the first day that carries something, when it is not.
  const isChoiceInMonth = chosenDate !== null && chosenDate.startsWith(prefix);
  const defaultDate = isSameMonth(shown, today) ? todayIso : (daysOfMonth[0]?.date ?? null);
  const selected = isChoiceInMonth ? chosenDate : defaultDate;
  const monthTotalMinor = daysOfMonth.reduce((sum, day) => sum + day.totalMinor, 0);
  const isMonthComplete = daysOfMonth.every((day) => day.isComplete);

  return (
    <Screen>
      <div className={styles.content}>
        <PageHeader title="Календар" />
        <PeriodSwitcher
          label={`${formatMonthTitle(formatMonthName(shown.month))} ${shown.year}`}
          previousLabel="Попередній місяць"
          nextLabel="Наступний місяць"
          onPrevious={() => {
            setShownMonth(shiftMonth(shown, -1));
            setChosenDate(null);
          }}
          onNext={() => {
            setShownMonth(shiftMonth(shown, 1));
            setChosenDate(null);
          }}
        />

        <p className={styles.monthTotal}>
          {daysOfMonth.length === 0
            ? 'Цього місяця підписки нічого не списують'
            : `Підписки за місяць: ${formatMoneyMaybeApproximate(monthTotalMinor, loaded.baseCurrency, !isMonthComplete)}`}
        </p>

        <MonthGrid
          year={shown.year}
          month={shown.month}
          daysByDate={daysByDate}
          today={todayIso}
          selected={selected}
          baseCurrency={loaded.baseCurrency}
          onSelect={setChosenDate}
        />

        {selected !== null && (
          <DayEntries
            day={daysByDate.get(selected)}
            baseCurrency={loaded.baseCurrency}
            onOpen={(paymentId) => {
              void navigate(paymentDetailPath(paymentId));
            }}
          />
        )}
      </div>
    </Screen>
  );
};
